<script setup>
/**
 * Hub settings — XN-4506.
 *
 * Everything on this page is identity: what the hub is called, where it sits in
 * time, and how somebody reaches it. Nothing here configures a feature.
 *
 * What left, and why it is not coming back:
 *  - Start and end dates. A hub does not begin and end any more, so the fields
 *    went and a migration emptied the two settings. The countdown, the Agenda's
 *    default date and the analytics payload each read an empty value as "no
 *    date set" rather than as a date of zero.
 *  - The OmniChat card. Displaying a member's email and allowing peer-to-peer
 *    messaging are messaging policy, so they moved to Communications, next to
 *    the code that already owns them.
 *
 * Anything with a page of its own — Registration & login, Policy lines, Team &
 * users — stays there. This page no longer links out to them: they are one
 * click away in the sidebar, and a shortcut to a neighbour is just clutter.
 */
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { isCompleteUsPhone, US_PHONE_MESSAGE } from '../../helpers/usPhone';
import { isValidEmail } from '../../helpers/helper';
import { useEventStore } from '../../stores/event.js';

import CharacterCounter from '../communications/CharacterCounter.vue';
import HubLogoCard from '../settings/HubLogoCard.vue';
import Loading from '../Loading.vue';
import PageHeader from '../PageHeader.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlField from '../xl/XlField.vue';

const NAME_MIN = 2;
const NAME_MAX = 50;

const BLANK = {
  login_event_name: '',
  login_event_timezone: '',
  login_event_contact_email: '',
  login_event_support_phone: '',
  // Two logos and their crops (XN-4507). See HubLogoCard.
  login_logo: '',
  login_logo_mark: '',
  login_logo_crop: '',
  login_logo_mark_crop: '',
};

const eventStore = useEventStore();
const { event } = storeToRefs(eventStore);

const timezones = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const formData = ref({ ...BLANK });
const savedData = ref({ ...BLANK });
const errors = ref({});

// The logo card edits four of the form's keys as one object.
const logoModel = computed({
  get: () => ({
    logo: formData.value.login_logo,
    logoMark: formData.value.login_logo_mark,
    logoCrop: formData.value.login_logo_crop,
    logoMarkCrop: formData.value.login_logo_mark_crop,
  }),
  set: (next) => {
    formData.value.login_logo = next.logo || '';
    formData.value.login_logo_mark = next.logoMark || '';
    formData.value.login_logo_crop = next.logoCrop || '';
    formData.value.login_logo_mark_crop = next.logoMarkCrop || '';
  },
});

// What the card may tidy is what no save has taken: it needs the saved shape too.
const savedLogoModel = computed(() => ({
  logo: savedData.value.login_logo,
  logoMark: savedData.value.login_logo_mark,
  logoCrop: savedData.value.login_logo_crop,
  logoMarkCrop: savedData.value.login_logo_mark_crop,
}));

const hasChanged = computed(() => Object.keys(BLANK)
  .some((key) => normalise(formData.value[key]) !== normalise(savedData.value[key])));

// The hub's name, not the draft in the box: a page title that rewrites itself
// on every keystroke is noise, and this line is naming the hub you are in.
const hubName = computed(() => savedData.value.login_event_name);

// Shaped for the select: the server decides the list, the alias is what a
// person reads. Same list as before the rebuild — the control around it is all
// that changed.
//
// A select cannot show a value it has no option for: it would fall back to the
// first one and quietly claim a zone the hub never chose. So when the stored
// value is missing or off the list, an explicit empty option leads, and the
// field is required — which turns a silent wrong answer into a question.
const timezoneOptions = computed(() => {
  const options = timezones.value.map((zone) => ({
    value: zone.timezone,
    label: zone.alias || zone.timezone,
  }));

  const current = formData.value.login_event_timezone;
  if (current && options.some((option) => option.value === current)) return options;

  return [{ value: '', label: 'Choose a time zone' }, ...options];
});

function normalise(value) {
  return (value ?? '').toString().trim();
}

// The guard in routes/guard.js reads this, and App.vue's dialog offers the Save
// below. A page that leaves a stale entry here blocks navigation everywhere, so
// it is cleared on unmount as well as on save.
watch(hasChanged, (dirty) => {
  window.store.historyChanges = dirty ? ['hub-settings'] : [];
});

/**
 * The rules the server enforces, said again here so the writer hears them
 * before the round trip rather than after it. The server is still the one that
 * decides — this only saves a wasted POST.
 */
function validate() {
  const found = {};
  const name = normalise(formData.value.login_event_name);
  const email = normalise(formData.value.login_event_contact_email);

  if (!name) {
    found.login_event_name = 'The hub needs a name.';
  } else if (name.length < NAME_MIN) {
    found.login_event_name = `The hub name needs at least ${NAME_MIN} characters.`;
  }

  if (!formData.value.login_event_timezone) {
    found.login_event_timezone = 'Choose a time zone.';
  }

  if (!email) {
    found.login_event_contact_email = 'A contact email is required.';
  } else if (!isValidEmail(email)) {
    found.login_event_contact_email = "That does not look like an email address.";
  }

  if (!isCompleteUsPhone(formData.value.login_event_support_phone)) {
    found.login_event_support_phone = US_PHONE_MESSAGE;
  }

  return found;
}

function buildPayload() {
  const payload = {
    login_event_name: normalise(formData.value.login_event_name),
    login_event_timezone: formData.value.login_event_timezone,
    login_event_contact_email: normalise(formData.value.login_event_contact_email),
    login_event_support_phone: normalise(formData.value.login_event_support_phone),
  };

  // A logo and its crop travel together, and only when this page changed
  // them: an absent key means "leave it" to the server. Two admins can have
  // the page open at once, and a save that always sent every logo would
  // put the older one's copy back over the newer one's — and then have the
  // server tidy away the files the newer copy had just been given.
  [['login_logo', 'login_logo_crop'], ['login_logo_mark', 'login_logo_mark_crop']].forEach(([logoKey, cropKey]) => {
    const touched = [logoKey, cropKey].some((key) => (formData.value[key] || '') !== (savedData.value[key] || ''));
    if (!touched) return;
    payload[logoKey] = formData.value[logoKey] || '';
    payload[cropKey] = formData.value[cropKey] || '';
  });

  return payload;
}

function syncEvent(payload) {
  if (event.value?.value) {
    event.value.value.login_event_name = payload.login_event_name;
    event.value.value.login_event_timezone = payload.login_event_timezone;
    event.value.value.login_event_contact_email = payload.login_event_contact_email;
    event.value.value.login_event_support_phone = payload.login_event_support_phone;
    event.value.value.login_logo = savedData.value.login_logo;
    event.value.value.login_logo_mark = savedData.value.login_logo_mark;
  }
}

function applyServerErrors(response) {
  if (response?.status !== 422 || !response.data?.errors) return;
  errors.value = Object.fromEntries(
    Object.entries(response.data.errors).map(([field, messages]) => [field, messages[0]])
  );
}

/**
 * @returns {Promise<boolean>} whether the save went through — App.vue's
 *   "leave this page?" dialog only continues on a true.
 */
async function handleSave() {
  errors.value = validate();

  const firstError = Object.values(errors.value)[0];
  if (firstError) {
    window.loadNotification({ message: firstError, error: true });
    return false;
  }

  isSaving.value = true;

  try {
    const payload = buildPayload();

    const { data } = await axios.post(`${window.store.event_backend_api}/hub-settings`, payload);

    // Nothing is reset from the response beyond what was sent: a failed save
    // has to leave every edit on screen, so the form is only ever caught up
    // after the server has agreed to it.
    savedData.value = { ...savedData.value, ...payload };
    formData.value = { ...formData.value, ...payload };
    errors.value = {};
    window.store.historyChanges = [];
    // The logo card learns what was saved through a prop, and it deletes
    // whatever it uploaded that no save has taken when it unmounts. Save-and-
    // leave navigates the moment this resolves, so resolve only once the
    // card has been told — or it would delete the files just made live.
    await nextTick();

    syncEvent(payload);
    window.store.subdomainName = payload.login_event_name;

    window.loadNotification({ message: data?.message || 'Hub settings saved' });
    return true;
  } catch (error) {
    const response = error?.response;
    applyServerErrors(response);

    window.loadNotification({
      message: Object.values(errors.value)[0] || response?.data?.message || 'Could not save. Nothing was changed.',
      error: true,
    });
    return false;
  } finally {
    isSaving.value = false;
  }
}

async function loadData() {
  window.store.pageHasChanges = false;
  window.store.historyChanges = [];

  try {
    const { data } = await axios.get(`${window.store.event_backend_api}/event-settings`, {
      params: { fields: 'login' },
    });

    timezones.value = data.timezones ?? [];

    const settings = data.settings ?? {};
    const loaded = { ...BLANK };
    Object.keys(BLANK).forEach((key) => {
      loaded[key] = settings[key] ?? '';
    });
    // A stored zone the list does not offer is dropped rather than carried.
    // The select cannot display it, so leaving it in the model meant the box
    // showed "Choose a time zone" while the value underneath was still the old
    // one — and the required check, which only tests truthiness, let it through
    // to the server's 422 instead of asking here.
    const offers = (zone) => timezones.value.some((option) => option.timezone === zone);
    if (loaded.login_event_timezone && !offers(loaded.login_event_timezone)) {
      loaded.login_event_timezone = '';
    }

    // The platform default is only a sensible pre-selection if the hub list
    // actually contains it — config('app.timezone') is UTC on a stock install,
    // which is not one of the nine zones a hub may sit in. Offering it would
    // mean saving a zone the picker cannot display.
    if (!loaded.login_event_timezone && offers(data.default_timezone)) {
      loaded.login_event_timezone = data.default_timezone;
    }

    formData.value = { ...loaded };
    savedData.value = { ...loaded };
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  // App.vue's leave dialog needs something to call for its Save button, and the
  // dialog lives outside this component's tree.
  window.store.saveCurrentPage = handleSave;
  loadData();
});

onUnmounted(() => {
  window.store.historyChanges = [];
  if (window.store.saveCurrentPage === handleSave) {
    window.store.saveCurrentPage = null;
  }
});
</script>

<template>
  <!-- The shell's top bar already names the section. The page's own title, the
       line under it and the Save that acts on it belong together, in the page. -->
  <PageHeader />

  <Loading v-if="isLoading" variant="form" :rows="6" />

  <div v-else class="settings" data-xl-gen="northstar">
    <header class="settings__head">
      <div class="settings__heading">
        <h1>Hub settings</h1>
        <p>{{ hubName }} · Registration and policy lines have their own pages.</p>
      </div>
      <XlButton :disabled="!hasChanged" :loading="isSaving" @click="handleSave">Save</XlButton>
    </header>

    <div class="settings__cols">
      <div class="settings__col">
        <XlCard padded>
          <div class="settings__card-head">
            <h2>Your hub</h2>
          </div>

          <div class="settings__fields">
            <XlField
              v-model="formData.login_event_name"
              name="login_event_name"
              label="Hub name"
              :maxlength="NAME_MAX"
              required
              :error="errors.login_event_name || ''"
              hint="Policyholders see this at the top of every page and on every email you send."
            >
              <template #counter>
                <CharacterCounter :value="formData.login_event_name" :max="NAME_MAX" plain-text />
              </template>
            </XlField>

            <XlField
              v-model="formData.login_event_timezone"
              type="select"
              name="login_event_timezone"
              label="Time zone"
              required
              :options="timezoneOptions"
              :error="errors.login_event_timezone || ''"
              hint="New sessions are scheduled in this zone. Everyone sees session times in their own time zone; renewal dates are calendar dates and never shift."
            />
          </div>
        </XlCard>

        <XlCard padded>
          <div class="settings__card-head">
            <h2>How people reach you</h2>
            <p>Shown in the portal footer and at the bottom of every email.</p>
          </div>

          <div class="settings__fields">
            <XlField
              v-model="formData.login_event_contact_email"
              type="email"
              name="login_event_contact_email"
              label="Contact email"
              required
              :error="errors.login_event_contact_email || ''"
            />

            <XlField
              v-model="formData.login_event_support_phone"
              type="tel"
              name="login_event_support_phone"
              label="Support phone"
              :error="errors.login_event_support_phone || ''"
              hint="The number a worried policyholder will ring. Make sure somebody answers it."
            />
          </div>
        </XlCard>
      </div>

      <div class="settings__col">
        <HubLogoCard v-model="logoModel" :saved-value="savedLogoModel" :hub-name="hubName" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* Spacing and type here follow Resources & Content, so the two pages read as
   one product: the shared page gutter (--setup-page-padding, the one
   .page-content gives every other page), a 30px title over a 15px line, 20px
   down to the first card, and the page painted in the background token rather
   than inheriting the grey the old admin shell puts behind everything. */
.settings {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: var(--setup-page-padding) var(--setup-page-padding) 24px;
  background: var(--xl-color-background, #fcfafd);
  font-family: var(--xl-font-family);
}

/* 32px under the description, which is what Resources & Content actually
   leaves: 12px of header-container gap plus the 20px padding-top on its
   content block.

   The 12px is the trap. pge-content-block sets that gap as an INLINE style,
   and Resources & Content's `:deep(.pge-cb-h-container) { gap: 6px }` cannot
   beat an inline declaration without !important — so the 6px never applies
   and reading it off the stylesheet gives an answer the page does not use. */
.settings__head {
  display: flex;
  align-items: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 32px;
}

.settings__heading {
  flex: 1;
  min-width: 230px;
  display: flex;
  flex-direction: column;
  /* The inline 12px the reference actually renders — see the note above. */
  gap: 12px;
}

/* The whole of the reference title's effective style, declared rather than
   inherited. font-feature-settings comes from the pge block's own base and is
   the one property Resources & Content does not override — so it was the one
   thing this heading did not share with it. */
.settings__heading h1 {
  margin: 0;
  font-family: var(--xl-font-family);
  font-feature-settings: 'clig' off, 'liga' off;
  font-size: var(--xl-page-title-size);
  font-style: normal;
  font-weight: 600;
  line-height: var(--xl-page-title-line);
  color: var(--xl-color-primary-text, #1c1720);
}

.settings__heading p {
  margin: 0;
  font-size: var(--xl-page-subtitle-size);
  line-height: var(--xl-page-subtitle-line);
  color: var(--xl-color-outline);
}

/* Equal halves, as the prototype's .grid2 has it. The two columns hold
   different amounts today only because the logo card is still the old
   single-slot picker; XN-4507's two slots and sidebar preview fill the right
   one out. minmax(0, …) rather than a bare 1fr so a long unbroken value in a
   field cannot push its column past its share. */
.settings__cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

@media (max-width: 900px) {
  .settings__cols {
    grid-template-columns: minmax(0, 1fr);
  }
}

.settings__col {
  min-width: 0;
}

/* A card's own heading, kept clear of the first field. */
.settings__card-head {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;

  h2 {
    margin: 0;
    font-size: var(--xl-type-body-large-size);
    line-height: var(--xl-type-body-large-line);
    font-weight: 600;
    color: var(--xl-color-primary-text);
  }

  p {
    margin: 0;
    font-size: var(--xl-type-body-medium-size);
    line-height: var(--xl-type-body-medium-line);
    color: var(--xl-color-secondary-text);
  }
}

/* One gap between stacked fields. The Content editors use 16px in a modal and
   18px in their page layout; 20px here, because a field carries a hint under
   it and the next label needs to read as a new question rather than more of
   the last answer. */
.settings__fields {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
</style>
